import { useState, useEffect } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;

const CATEGORY_OPTIONS = ['Bags', 'Drinkware', 'Apparel', 'Tech', 'Office', 'Writing', 'Awards', 'Health & Wellness', 'Outdoor', 'Tools'];

// ─── BundleBuilder ────────────────────────────────────────────────────────────
// Distributor-facing AI tool. Fills out a brief, gets 2-3 product bundles back,
// clicks "Use this bundle" to populate the cart and jump to the presentation
// builder — same flow as turning a quote request into a presentation.
// Internal-only — never visible on customer-facing storefront.
export default function BundleBuilder({ getToken, showToast, navigate, setCart }) {
  // Form inputs (Read B — every prompt is inline, not a setting)
  const [budget, setBudget]               = useState('');
  const [recipients, setRecipients]       = useState('');
  const [audience, setAudience]           = useState('');
  const [useCase, setUseCase]             = useState('');
  const [excludeCats, setExcludeCats]     = useState([]);
  const [bundleCount, setBundleCount]     = useState(3);
  const [strategy, setStrategy]           = useState('distinct');
  const [allowSingle, setAllowSingle]     = useState(true);
  const [strictBudget, setStrictBudget]   = useState(false);
  // New inputs (added per distributor request — Cooper's-email-style briefs need richer context)
  const [itemsPerBundle, setItemsPerBundle] = useState(''); // empty = let AI decide
  const [mustInclude, setMustInclude]     = useState('');
  const [decoration, setDecoration]       = useState('any');
  const [notes, setNotes]                 = useState('');
  const [destZip, setDestZip]             = useState(''); // required for shipping estimates

  // Result state
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [bundles, setBundles]   = useState(null);
  const [meta, setMeta]         = useState(null);
  const [creatingId, setCreatingId] = useState(null); // which bundle is currently being turned into a presentation

  // Block accidental tab-close while AI is generating. Most browsers ignore the
  // custom message but will still show their own "leave site?" confirm dialog.
  useEffect(() => {
    if (!loading) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [loading]);

  const toggleCategory = (cat) => {
    setExcludeCats(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  const submit = async () => {
    setError(null);
    if (!budget || Number(budget) <= 0)         { setError('Enter a budget.'); return; }
    if (!recipients || Number(recipients) <= 0) { setError('Enter the number of recipients.'); return; }
    if (!audience.trim())                       { setError('Tell the AI who the recipients are.'); return; }
    if (!destZip || destZip.length !== 5)       { setError('Enter a 5-digit destination zip code for shipping estimates.'); return; }

    setLoading(true);
    setBundles(null);
    setMeta(null);
    try {
      const t = await getToken();
      const j = await api.withToken(t).bundleSuggest({
        budget: Number(budget),
        recipients: Number(recipients),
        audience: audience.trim(),
        use_case: useCase.trim(),
        exclude_categories: excludeCats,
        bundle_count: bundleCount,
        strategy,
        allow_single_product: allowSingle,
        strict_budget: strictBudget,
        items_per_bundle: itemsPerBundle ? Number(itemsPerBundle) : undefined,
        must_include: mustInclude.trim() || undefined,
        decoration: decoration === 'any' ? undefined : decoration,
        notes: notes.trim() || undefined,
        dest_zip: destZip,
      });
      setBundles(j.bundles || []);
      setMeta({ budget: j.budget, recipients: j.recipients, notice: j.notice });
    } catch (e) {
      const isQuotaError = /AI Bundle search limit reached/i.test(e.message || '');
      if (isQuotaError) {
        setError("You've hit your AI Bundle Builder limit for this billing period. Upgrade your plan or wait until renewal.");
        showToast?.('Bundle limit reached — upgrade for more', 'error');
        // Soft nudge to billing — don't force redirect; lets them see the
        // error in context. Distributors can navigate from sidebar.
      } else {
        setError(e.message);
        showToast?.('Bundle generation failed: ' + e.message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Turn a bundle into a populated cart, then jump to the presentations page.
  // Mirrors buildPresentationFromQuote in App.jsx — same pattern, same UX.
  // Does NOT write to the DB; the distributor names + saves the presentation
  // themselves in the builder.
  const useBundle = async (bundle, idx) => {
    setCreatingId(idx);
    try {
      const t = await getToken();
      // Hydrate full product records (bundle data is trimmed; presentation
      // builder needs full pricing tiers, colors, images, supplier_charges).
      const ids = bundle.items.map(i => i.id);
      const hyd = await api.withToken(t).productsByIds(ids);
      const fullProducts = hyd.results || [];
      const byId = Object.fromEntries(fullProducts.map(p => [p.id, p]));

      // Annotate each hydrated product with the qty Claude recommended.
      // Color is left empty — distributor picks per-client in the builder.
      const cartItems = bundle.items.map(item => {
        const full = byId[item.id];
        if (!full) return null;
        return { ...full, qty: item.qty, client_color: null };
      }).filter(Boolean);

      if (cartItems.length === 0) {
        showToast?.('Could not load products from this bundle', 'error');
        return;
      }

      setCart(cartItems);
      showToast?.(`Loaded ${cartItems.length} item${cartItems.length===1?'':'s'} from "${bundle.label}"`);
      navigate('presentations');
    } catch (e) {
      showToast?.('Failed to load bundle: ' + e.message, 'error');
    } finally {
      setCreatingId(null);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 600, color: '#0F172A', marginBottom: 4 }}>
          AI Bundle Builder
        </div>
        <div style={{ fontSize: 13, color: '#64748B' }}>
          Describe the gift, get 2-3 bundle ideas. Pick one to start a presentation — you can edit anything afterward.
        </div>
      </div>

      {/* Form card */}
      <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          <Field label="Budget (client-facing total)">
            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #E2E8F0', borderRadius: 8, paddingLeft: 12 }}>
              <span style={{ color: '#94A3B8', fontSize: 13 }}>$</span>
              <input type="number" value={budget} onChange={e => setBudget(e.target.value)}
                placeholder="500" min="1"
                style={{ flex: 1, border: 'none', padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', color: '#0F172A' }}/>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, color: '#475569', cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={strictBudget} onChange={e => setStrictBudget(e.target.checked)} style={{ cursor: 'pointer' }}/>
              <span>Strict budget — bundles must not exceed this amount</span>
            </label>
          </Field>
          <Field label="How many recipients?">
            <input type="number" value={recipients} onChange={e => setRecipients(e.target.value)}
              placeholder="100" min="1"
              style={inputStyle}/>
          </Field>
          <Field label="Destination zip code (required)">
            <input value={destZip}
              onChange={e => setDestZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
              placeholder="98101"
              maxLength={5}
              inputMode="numeric"
              style={inputStyle}/>
            <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>For shipping estimates</div>
          </Field>
        </div>

        <Field label="Who are the recipients?">
          <input value={audience} onChange={e => setAudience(e.target.value)}
            placeholder="e.g. real estate clients, tech employees, conference attendees"
            style={inputStyle}/>
        </Field>

        <Field label="Event / use case (optional)">
          <input value={useCase} onChange={e => setUseCase(e.target.value)}
            placeholder="e.g. outdoor open house, holiday gift, trade show booth"
            style={inputStyle}/>
        </Field>

        <Field label="Exclude categories (optional)">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {CATEGORY_OPTIONS.map(cat => {
              const on = excludeCats.includes(cat);
              return (
                <button key={cat} type="button" onClick={() => toggleCategory(cat)}
                  style={{
                    border: `1px solid ${on ? '#FCA5A5' : '#E2E8F0'}`,
                    background: on ? '#FEE2E2' : '#fff',
                    color: on ? '#B91C1C' : '#475569',
                    borderRadius: 999, padding: '4px 10px', fontSize: 12,
                    fontFamily: 'inherit', cursor: 'pointer',
                  }}>
                  {on ? '✕ ' : ''}{cat}
                </button>
              );
            })}
          </div>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          <Field label="Items per bundle (optional)">
            <select value={itemsPerBundle} onChange={e => setItemsPerBundle(e.target.value)} style={inputStyle}>
              <option value="">Let AI decide</option>
              {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n} item{n>1?'s':''} per bundle</option>)}
            </select>
          </Field>
          <Field label="Decoration / logo">
            <select value={decoration} onChange={e => setDecoration(e.target.value)} style={inputStyle}>
              <option value="any">Doesn't matter</option>
              <option value="1color">1-color logo</option>
              <option value="multicolor">Multi-color (2-4 colors)</option>
              <option value="fullcolor">Full-color / digital print</option>
            </select>
          </Field>
          <Field label="Must include (optional)">
            <input value={mustInclude} onChange={e => setMustInclude(e.target.value)}
              placeholder="e.g. T-shirts, hats, stickers"
              style={inputStyle}/>
          </Field>
        </div>

        <Field label="Anything else the AI should know? (optional)">
          <textarea value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Paste relevant parts of an email, special requirements, color preferences, deadline pressure, etc."
            rows={3}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}/>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginTop: 8 }}>
          <Field label="How many bundles?">
            <select value={bundleCount} onChange={e => setBundleCount(Number(e.target.value))} style={inputStyle}>
              <option value={2}>2 bundles</option>
              <option value={3}>3 bundles</option>
              <option value={4}>4 bundles</option>
            </select>
          </Field>
          <Field label="Strategy">
            <select value={strategy} onChange={e => setStrategy(e.target.value)} style={inputStyle}>
              <option value="distinct">Distinct (value / balanced / premium)</option>
              <option value="freeform">Freeform (AI's call)</option>
            </select>
          </Field>
          <Field label="Single-product bundles?">
            <select value={allowSingle ? 'yes' : 'no'} onChange={e => setAllowSingle(e.target.value === 'yes')} style={inputStyle}>
              <option value="yes">Allowed</option>
              <option value="no">Require variety</option>
            </select>
          </Field>
        </div>

        {error && (
          <div style={{ marginTop: 12, padding: 10, background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 8, fontSize: 12, color: '#B91C1C' }}>
            {error}
          </div>
        )}

        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={submit} disabled={loading}
            style={{
              background: G, border: 'none', borderRadius: 8, padding: '10px 24px',
              color: '#fff', fontSize: 13, fontWeight: 500,
              cursor: loading ? 'wait' : 'pointer', fontFamily: 'inherit',
              opacity: loading ? 0.7 : 1,
            }}>
            {loading ? 'Generating bundles…' : '✨ Generate Bundles'}
          </button>
        </div>
      </div>

      {/* Bundle results */}
      {bundles && bundles.length > 0 && (
        <div>
          <div style={{ fontSize: 14, fontWeight: 500, color: '#0F172A', marginBottom: 8 }}>
            {bundles.length} bundle{bundles.length === 1 ? '' : 's'} for ${meta?.budget?.toFixed(2)} budget · {meta?.recipients} recipients
          </div>
          {/* Disclaimer — visible whenever bundles render. Setup + shipping are
              estimates; distributors should always verify with suppliers before
              committing to a quote. */}
          <div style={{
            background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8,
            padding: '8px 12px', fontSize: 12, color: '#92400E',
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
          }}>
            <span style={{ fontSize: 14 }}>⚠</span>
            <span>All costs are estimates. Setup and shipping should be verified with suppliers before quoting.</span>
          </div>
          {/* Constraint-feasibility notice (shows when fewer bundles than requested
              were found — e.g. tight budget × many items per bundle). */}
          {meta?.notice && (
            <div style={{
              background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8,
              padding: '8px 12px', fontSize: 12, color: '#1E40AF',
              display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 16,
            }}>
              <span style={{ fontSize: 14, lineHeight: 1.4 }}>ℹ</span>
              <span>{meta.notice}</span>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(bundles.length, 3)}, 1fr)`, gap: 16 }}>
            {bundles.map((b, idx) => (
              <BundleCard key={idx} bundle={b} budget={meta?.budget} idx={idx}
                onUse={() => useBundle(b, idx)}
                creating={creatingId === idx}/>
            ))}
          </div>
        </div>
      )}

      {/* Loading modal — shown over the page while AI is working. Centered card,
          dimmed backdrop. The backdrop captures all clicks so the user can't
          accidentally interact with the form behind it. We also wire a
          beforeunload handler in the parent useEffect so closing the tab
          shows a browser confirm. */}
      {loading && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(15, 23, 42, 0.55)',
          display:'flex', alignItems:'center', justifyContent:'center',
          zIndex: 1000, padding: 16,
        }}>
          <div style={{
            background:'#fff', borderRadius:14, padding:'28px 32px',
            maxWidth:380, width:'100%', boxShadow:'0 20px 50px rgba(0,0,0,0.3)',
            textAlign:'center',
          }}>
            <div style={{ fontSize:32, marginBottom:14 }}>✨</div>
            <div style={{ fontSize:16, fontWeight:600, color:'#0F172A', marginBottom:8 }}>
              Putting your bundles together
            </div>
            <div style={{ fontSize:13, color:'#64748B', lineHeight:1.5, marginBottom:18 }}>
              The AI is reviewing the catalog and assembling {bundleCount} option{bundleCount>1?'s':''} for {recipients || 'your'} recipients.
              This usually takes 10-30 seconds.
            </div>
            <div style={{
              fontSize:11, color:'#94A3B8',
              padding:'8px 12px', background:'#F8FAFC', borderRadius:8,
              display:'inline-block',
            }}>
              Please don't close this tab
            </div>
            {/* Animated dots — pure CSS, no library needed. */}
            <div style={{ marginTop:18, display:'flex', justifyContent:'center', gap:6 }}>
              {[0,1,2].map(i => (
                <div key={i} style={{
                  width:8, height:8, borderRadius:'50%', background:G,
                  animation:`bundle-dot 1.4s ease-in-out ${i * 0.2}s infinite`,
                }}/>
              ))}
            </div>
            <style>{`
              @keyframes bundle-dot {
                0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
                40% { opacity: 1; transform: scale(1); }
              }
            `}</style>
          </div>
        </div>
      )}

      {bundles && bundles.length === 0 && (
        <div style={{ textAlign: 'center', padding: 48, color: '#94A3B8', fontSize: 13 }}>
          No usable bundles came back — try adjusting the budget or audience and retrying.
        </div>
      )}
    </div>
  );
}

// ─── BundleCard ───────────────────────────────────────────────────────────────
function BundleCard({ bundle, budget, idx, onUse, creating }) {
  const colors = ['#10B981', '#3B82F6', '#F59E0B', '#8B5CF6'];
  const accent = colors[idx % colors.length];
  const overBudget = budget && bundle.total_estimated > budget;

  // Aggregate any per-item shipping warnings (e.g. "no FOB origin" or rough estimates)
  // into one badge — listing every item's warning would clutter the card.
  const shippingWarnings = [...new Set(
    bundle.items.map(i => i.shipping_warning).filter(Boolean)
  )];
  const hasShippingIssue = shippingWarnings.length > 0 || bundle.items_missing_shipping > 0;
  const hasSetupGaps    = bundle.items_missing_setup > 0;

  return (
    <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: `${accent}10`, borderBottom: `2px solid ${accent}`, padding: '12px 16px' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#0F172A' }}>{bundle.label}</div>
        <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>{bundle.rationale}</div>
      </div>

      <div style={{ flex: 1, padding: 12 }}>
        {bundle.items.map(item => (
          <div key={item.id} style={{ display: 'flex', gap: 10, marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid #F1F5F9' }}>
            <div style={{ width: 48, height: 48, borderRadius: 6, background: '#F8FAFC', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {item.image
                ? <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }}/>
                : <span style={{ fontSize: 18 }}>📦</span>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.name}
              </div>
              <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                {item.qty} × ${item.unit_client_price.toFixed(2)} = ${item.line_total_client.toFixed(2)}
              </div>
              {/* Per-line setup + shipping (small, muted) */}
              <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span>Setup: {item.setup_client !== null ? `$${item.setup_client.toFixed(2)}` : <span style={{ color: '#CA8A04' }}>N/A</span>}</span>
                <span>Ship: {item.shipping_estimate !== null ? `$${item.shipping_estimate.toFixed(2)}` : <span style={{ color: '#CA8A04' }}>N/A</span>}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Breakdown footer */}
      <div style={{ padding: 12, borderTop: '1px solid #E2E8F0', background: '#F8FAFC' }}>
        <div style={{ fontSize: 11, color: '#64748B', display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
          <span>Products</span>
          <span style={{ color: '#0F172A', fontWeight: 500 }}>${bundle.products_subtotal.toFixed(2)}</span>
        </div>
        <div style={{ fontSize: 11, color: '#64748B', display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
          <span>Setup {hasSetupGaps && <span style={{ color: '#CA8A04' }}>({bundle.items_missing_setup} missing)</span>}</span>
          <span style={{ color: '#0F172A', fontWeight: 500 }}>${bundle.setup_subtotal.toFixed(2)}</span>
        </div>
        <div style={{ fontSize: 11, color: '#64748B', display: 'flex', justifyContent: 'space-between', marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #E2E8F0' }}>
          <span>Shipping (est.) {hasShippingIssue && <span style={{ color: '#CA8A04' }}>⚠</span>}</span>
          <span style={{ color: '#0F172A', fontWeight: 500 }}>${bundle.shipping_subtotal.toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>Estimated Total</span>
          <span style={{ fontSize: 18, fontWeight: 600, color: overBudget ? '#B91C1C' : '#059669' }}>
            ${bundle.total_estimated.toFixed(2)}
          </span>
        </div>
        {budget && (
          <div style={{ fontSize: 11, color: overBudget ? '#B91C1C' : '#059669', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>{overBudget ? '⚠' : '✓'}</span>
            <span>{bundle.budget_pct}% of ${budget.toFixed(0)} budget</span>
            {bundle.closest_to_budget && (
              <span style={{ marginLeft: 'auto', background: '#FEF3C7', color: '#92400E', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 500 }}>
                Closest fit
              </span>
            )}
          </div>
        )}
        <button onClick={onUse} disabled={creating}
          style={{
            width: '100%', background: accent, border: 'none', borderRadius: 8,
            padding: '9px 12px', color: '#fff', fontSize: 13, fontWeight: 500,
            cursor: creating ? 'wait' : 'pointer', fontFamily: 'inherit',
            opacity: creating ? 0.7 : 1,
          }}>
          {creating ? 'Creating presentation…' : 'Use this bundle →'}
        </button>
      </div>
    </div>
  );
}

// ─── Shared form bits ─────────────────────────────────────────────────────────
const inputStyle = {
  width: '100%', border: '1px solid #E2E8F0', borderRadius: 8, padding: '9px 12px',
  fontSize: 13, fontFamily: 'inherit', outline: 'none', color: '#0F172A', background: '#fff',
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5, display: 'block' }}>
        {label}
      </label>
      {children}
    </div>
  );
}
